import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/orgApi";
import {
  fetchOrganizations, formToPayload, resourceToForm,
  LOCATION_TYPES, PHYSICAL_TYPES, LOCATION_STATUSES,
} from "@/lib/locationApi";
import { LOC } from "@/constants/testIds";

const BLANK = {
  name: "", status: "active", type: "OF", physicalType: "bu",
  managingOrgId: "", description: "", alias: "",
  phone: "", fax: "", email: "",
  line1: "", line2: "", city: "", state: "", postalCode: "", country: "US",
};

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-teal-700">{title}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, id, value, onChange, testId, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </Label>
      <Input id={id} data-testid={testId} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default function LocationForm({ open, onClose, editing, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    fetchOrganizations().then((list) => {
      setOrgs(list);
      setF((prev) => {
        const base = editing ? resourceToForm(editing) : BLANK;
        if (!base.managingOrgId && list.length === 1) base.managingOrgId = list[0].id;
        return base;
      });
    });
  }, [open, editing]);

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required (FHIR 1..1).");
    if (!f.managingOrgId) return toast.error("Managing organization is required — each Location must belong to an Organization.");
    const payload = formToPayload(f, orgs);
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/locations/${editing.id}`, payload);
        toast.success("Location updated.");
      } else {
        await api.post("/locations", payload);
        toast.success("Location created.");
      }
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save location.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={LOC.form}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Location" : "New Location"}</DialogTitle>
          <DialogDescription>FHIR R4 Location · US Core profile · building/site level</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <Section title="Identity">
            <div className="sm:col-span-2">
              <Field label="Name" id="loc-name" required value={f.name}
                onChange={set("name")} testId={LOC.fName} placeholder="Brooklyn Office" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Managing Organization <span className="text-rose-600">*</span></Label>
              <Select value={f.managingOrgId} onValueChange={set("managingOrgId")}>
                <SelectTrigger data-testid={LOC.fOrg}><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Status</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid={LOC.fStatus}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_STATUSES.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Type</Label>
              <Select value={f.type} onValueChange={set("type")}>
                <SelectTrigger data-testid={LOC.fType}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Physical Type</Label>
              <Select value={f.physicalType} onValueChange={set("physicalType")}>
                <SelectTrigger data-testid={LOC.fPhysical}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PHYSICAL_TYPES.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field label="Alias (comma separated)" id="loc-alias" value={f.alias}
              onChange={set("alias")} testId={LOC.fAlias} placeholder="Brooklyn, BK" />
            <div className="sm:col-span-2 space-y-1.5">
              <Label htmlFor="loc-desc" className="text-sm text-slate-700">Description</Label>
              <Textarea id="loc-desc" data-testid={LOC.fDescription} value={f.description}
                onChange={(e) => set("description")(e.target.value)}
                placeholder="Outpatient pain management clinic" />
            </div>
          </Section>

          <Section title="Telecom">
            <Field label="Phone" id="loc-phone" value={f.phone} onChange={set("phone")}
              testId={LOC.fPhone} placeholder="+1-718-555-0111" />
            <Field label="Fax" id="loc-fax" value={f.fax} onChange={set("fax")}
              testId={LOC.fFax} placeholder="+1-718-555-0112" />
            <Field label="Email" id="loc-email" value={f.email} onChange={set("email")}
              testId={LOC.fEmail} placeholder="brooklyn@example.com" />
          </Section>

          <Section title="Address">
            <div className="sm:col-span-2">
              <Field label="Address line 1" id="loc-line1" value={f.line1}
                onChange={set("line1")} testId={LOC.fLine1} placeholder="100 Main St" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Address line 2" id="loc-line2" value={f.line2}
                onChange={set("line2")} testId={LOC.fLine2} placeholder="" />
            </div>
            <Field label="City" id="loc-city" value={f.city} onChange={set("city")}
              testId={LOC.fCity} placeholder="Brooklyn" />
            <Field label="State" id="loc-state" value={f.state} onChange={set("state")}
              testId={LOC.fState} placeholder="NY" />
            <Field label="Postal code" id="loc-postal" value={f.postalCode}
              onChange={set("postalCode")} testId={LOC.fPostal} placeholder="11201" />
            <Field label="Country" id="loc-country" value={f.country}
              onChange={set("country")} testId={LOC.fCountry} placeholder="US" />
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={LOC.cancelBtn}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={LOC.saveBtn}
            className="bg-teal-700 hover:bg-teal-800">
            {saving ? "Saving…" : editing ? "Save changes" : "Create location"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
